import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import dayjs from "dayjs";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import Overlay from "components/Overlay/Overlay";

const LinguistsGeneratedDocuments = () => {
  usePageTitle("Linguist Generated Documents");

  const theme = useTheme();
  const { overlayData, setOverlayName, showSnackbar } = useStateContext();
  
  const linguistId = Number(overlayData?.LinguistID || overlayData?.Linguist?.id || 0);
  const linguist = overlayData?.Linguist || {};
  const isAllLinguistsMode = !linguistId || String(overlayData?.mode || '').toLowerCase() === 'all';

  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // "Load more" pagination: the server supports `limit` but no offset, so we
  // grow the requested limit and refetch. Server caps: 100 per linguist, 500 all.
  const PAGE_SIZE = isAllLinguistsMode ? 150 : 50;
  const MAX_LIMIT = isAllLinguistsMode ? 500 : 100;
  const [visibleLimit, setVisibleLimit] = useState(PAGE_SIZE);
  const hasMoreDocuments =
    documents.length >= visibleLimit && visibleLimit < MAX_LIMIT;

  const loadMoreDocuments = () => {
    setVisibleLimit((prev) => Math.min(prev + PAGE_SIZE, MAX_LIMIT));
  };

  // Reset the visible limit when switching between all-linguists and single-linguist mode
  useEffect(() => {
    setVisibleLimit(isAllLinguistsMode ? 150 : 50);
  }, [isAllLinguistsMode]);

  const filteredDocuments = useMemo(() => {
    if (!isAllLinguistsMode) return documents;

    const query = String(searchQuery || "").trim().toLowerCase();
    if (!query) return documents;

    return documents.filter((doc) => {
      const linguistName = String(doc?.linguistName || "").toLowerCase();
      const templateName = String(doc?.templateName || "").toLowerCase();
      const originalFileName = String(doc?.originalFileName || "").toLowerCase();
      return linguistName.includes(query) || templateName.includes(query) || originalFileName.includes(query);
    });
  }, [documents, isAllLinguistsMode, searchQuery]);

  const groupedDocuments = useMemo(() => {
    if (!isAllLinguistsMode) return [];

    const groupsMap = new Map();
    filteredDocuments.forEach((doc) => {
      const linguistId = Number(doc?.linguistId || 0);
      const linguistName = String(doc?.linguistName || "-").trim() || "-";
      const key = `${linguistId}-${linguistName}`;

      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          key,
          linguistId,
          linguistName,
          documents: [],
        });
      }

      groupsMap.get(key).documents.push(doc);
    });

    return [...groupsMap.values()]
      .map((group) => ({
        ...group,
        documents: [...group.documents].sort((left, right) => {
          const leftTime = left?.createdOn ? new Date(left.createdOn).getTime() : 0;
          const rightTime = right?.createdOn ? new Date(right.createdOn).getTime() : 0;
          return rightTime - leftTime;
        }),
      }))
      .sort((left, right) => left.linguistName.localeCompare(right.linguistName));
  }, [filteredDocuments, isAllLinguistsMode]);

  const closeOverlay = () => setOverlayName("");

  useEffect(() => {
    let mounted = true;

    const fetchDocuments = async () => {
      if (!isAllLinguistsMode && !linguistId) {
        setDocuments([]);
        return;
      }

      setLoadingDocuments(true);
      try {
        const endpoint = isAllLinguistsMode
          ? `/documentTemplates/generated?limit=${visibleLimit}`
          : `/documentTemplates/generated/linguist/${linguistId}?limit=${visibleLimit}`;
        const response = await api.get(endpoint);
        if (!mounted) return;
        setDocuments(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        if (mounted) {
          setDocuments([]);
          showSnackbar("Unable to load generated documents", theme, "error");
        }
      } finally {
        if (mounted) setLoadingDocuments(false);
      }
    };

    fetchDocuments();

    return () => {
      mounted = false;
    };
  }, [isAllLinguistsMode, linguistId, visibleLimit, showSnackbar, theme]);

  const handleDownloadDocument = async (documentId, fileName) => {
    try {
      const response = await api.get(
        `/documentTemplates/generated/${documentId}/file?mode=download`,
        { responseType: "blob" },
      );

      const contentDisposition = response?.headers?.["content-disposition"] || "";
      const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const resolvedFileName = fileNameMatch?.[1] || String(fileName || `document_${documentId}.pdf`);

      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = resolvedFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      const message = error?.response?.data?.error || "Unable to download generated document";
      showSnackbar(message, theme, "error");
    }
  };

  return (
    <Overlay
      id="linguists-generated-documents"
      open={true}
      onClose={closeOverlay}
      title="Generated Documents"
      height="fit-content"
      maxWidthPx={1000}
    >
        <Box
          sx={{
            p: { xs: "0.75rem", sm: "1rem 2.3rem 1rem 2.5rem" },
            width: "100%",
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h5" fontWeight={600}>
                {isAllLinguistsMode ? 'Linguists: All' : `Linguist: ${linguist?.Name || '-'}`}
              </Typography>
            </Grid>

            {isAllLinguistsMode && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search linguist, template, or file"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.primary.border2}`,
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
              >
                {loadingDocuments ? (
                  <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={22} />
                  </Box>
                ) : filteredDocuments.length === 0 ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {isAllLinguistsMode
                        ? (String(searchQuery || "").trim()
                          ? 'No documents match your search.'
                          : 'No generated documents found yet.')
                        : 'No generated documents found yet for this linguist.'}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: 480, overflowY: "auto", p: 1 }}>
                    {isAllLinguistsMode ? groupedDocuments.map((group) => (
                      <Box
                        key={group.key}
                        sx={{
                          border: `1px solid ${theme.palette.primary.border2}`,
                          borderRadius: "10px",
                          p: 1,
                          mb: 1,
                          bgcolor: theme.palette.background.paper,
                          "&:last-of-type": {
                            mb: 0,
                          },
                        }}
                      >
                        <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                          {group.linguistName}
                        </Typography>

                        {group.documents.map((doc) => {
                          const createdLabel = doc?.createdOn
                            ? dayjs.tz(doc.createdOn).format("M/D/YYYY h:mm A")
                            : "N/A";
                          const expiresLabel = doc?.expiresAt
                            ? dayjs.tz(doc.expiresAt).format("M/D/YYYY h:mm A")
                            : "N/A";
                          const expiredLabel = doc?.expiredOn
                            ? dayjs.tz(doc.expiredOn).format("M/D/YYYY h:mm A")
                            : expiresLabel;
                          const isSuccess = String(doc?.status || "").toLowerCase() === "success";
                          const isExpired = String(doc?.status || "").toLowerCase() === "expired";
                          const createdByName = doc?.createdByName || "N/A";
                          const mergedTemplates = Array.isArray(doc?.mergedTemplates) ? doc.mergedTemplates : [];
                          const isMergedDocument = mergedTemplates.length > 1;
                          const displayTemplateName = isMergedDocument
                            ? "Merged Document"
                            : (doc?.templateName || "Template");
                          const templatesUsedLabel = mergedTemplates
                            .map((item) => String(item?.templateName || "").trim())
                            .filter(Boolean)
                            .join(", ");

                          return (
                            <Box
                              key={doc.id}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 1,
                                p: 1,
                                mb: 0.75,
                                borderRadius: "10px",
                                border: `1px solid ${isExpired ? theme.palette.error.main : theme.palette.primary.border2}`,
                                bgcolor: theme.palette.background.default,
                                boxShadow: theme.shadows[1],
                                opacity: isExpired ? 0.7 : 1,
                                "&:last-of-type": {
                                  mb: 0,
                                },
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>
                                  {displayTemplateName}
                                </Typography>
                                {templatesUsedLabel && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", whiteSpace: "normal", overflowWrap: "anywhere" }}
                                  >
                                    Templates: {templatesUsedLabel}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                  Created: {createdLabel}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                  Generated by: {createdByName}
                                </Typography>
                                {isExpired ? (
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    fontWeight={700}
                                    sx={{ display: "block" }}
                                  >
                                    Expired: {expiredLabel}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                    Expires: {expiresLabel}
                                  </Typography>
                                )}
                              </Box>

                              <Tooltip title={isExpired ? "Expired" : (isSuccess ? "Download" : "Not available")}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={!isSuccess}
                                    onClick={() => handleDownloadDocument(doc.id, doc?.originalFileName)}
                                  >
                                    <DownloadRoundedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          );
                        })}
                      </Box>
                    )) : filteredDocuments.map((doc) => {
                      const createdLabel = doc?.createdOn
                        ? dayjs.tz(doc.createdOn).format("M/D/YYYY h:mm A")
                        : "N/A";
                      const expiresLabel = doc?.expiresAt
                        ? dayjs.tz(doc.expiresAt).format("M/D/YYYY h:mm A")
                        : "N/A";
                      const expiredLabel = doc?.expiredOn
                        ? dayjs.tz(doc.expiredOn).format("M/D/YYYY h:mm A")
                        : expiresLabel;
                      const isSuccess = String(doc?.status || "").toLowerCase() === "success";
                      const isExpired = String(doc?.status || "").toLowerCase() === "expired";
                      const createdByName = doc?.createdByName || "N/A";
                      const mergedTemplates = Array.isArray(doc?.mergedTemplates) ? doc.mergedTemplates : [];
                      const isMergedDocument = mergedTemplates.length > 1;
                      const displayTemplateName = isMergedDocument
                        ? "Merged Document"
                        : (doc?.templateName || "Template");
                      const templatesUsedLabel = mergedTemplates
                        .map((item) => String(item?.templateName || "").trim())
                        .filter(Boolean)
                        .join(", ");

                      return (
                        <Box
                          key={doc.id}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                            p: 1,
                            mb: 0.75,
                            borderRadius: "10px",
                            border: `1px solid ${isExpired ? theme.palette.error.main : theme.palette.primary.border2}`,
                            bgcolor: theme.palette.background.default,
                            opacity: isExpired ? 0.7 : 1,
                            "&:last-of-type": {
                              mb: 0,
                            },
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {displayTemplateName}
                            </Typography>
                            {templatesUsedLabel && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", whiteSpace: "normal", overflowWrap: "anywhere" }}
                              >
                                Templates: {templatesUsedLabel}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              Created: {createdLabel}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              Generated by: {createdByName}
                            </Typography>
                            {isExpired ? (
                              <Typography
                                variant="caption"
                                color="error"
                                fontWeight={700}
                                sx={{ display: "block" }}
                              >
                                Expired: {expiredLabel}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                Expires: {expiresLabel}
                              </Typography>
                            )}
                          </Box>

                          <Tooltip title={isExpired ? "Expired" : (isSuccess ? "Download" : "Not available")}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={!isSuccess}
                                onClick={() => handleDownloadDocument(doc.id, doc?.originalFileName)}
                              >
                                <DownloadRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>

              {!loadingDocuments && filteredDocuments.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    mt: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Showing {filteredDocuments.length} document
                    {filteredDocuments.length === 1 ? "" : "s"}
                  </Typography>
                  {hasMoreDocuments && (
                    <Button size="small" onClick={loadMoreDocuments}>
                      Load more
                    </Button>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
    </Overlay>
  );
};

export default LinguistsGeneratedDocuments;
